import { useMemo, useState } from 'react';
import EquityCurve from '@/components/feature/EquityCurve';
import DrawdownCurve from '@/components/feature/DrawdownCurve';
import { computeDrawdown, sliceByRange, type EquityPoint, type RangeWindow } from '@/utils/weeklyEquity';

type ViewMode = 'week' | 'month';

const TABS: { id: ViewMode; label: string }[] = [
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' },
];

const RANGE_TABS: { id: RangeWindow; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: '12m', label: 'Last 12M' },
];

interface EquityAndDrawdownProps {
  weekly: EquityPoint[];
  monthly: EquityPoint[];
}

export default function EquityAndDrawdown({ weekly, monthly }: EquityAndDrawdownProps) {
  const [mode, setMode] = useState<ViewMode>('week');
  const [range, setRange] = useState<RangeWindow>('all');

  const fullEquity = mode === 'week' ? weekly : monthly;
  const equity = useMemo(() => sliceByRange(fullEquity, range), [fullEquity, range]);
  const drawdown = useMemo(() => computeDrawdown(equity), [equity]);
  const maxDrawdown = drawdown.length ? Math.min(...drawdown.map((d) => d.value)) : 0;
  const pointNoun = mode === 'week' ? 'Week' : 'Month';

  return (
    <div>
      {/* view switcher */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="min-w-0 text-xs md:text-sm text-foreground-500">
          {mode === 'week'
            ? 'One point per trading week — zoom in on the swings'
            : 'One point per month — the longer-term trend'}
          {range === '12m' ? ' · trailing 12 months' : ' · full history'}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-full bg-background-200/70 p-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setMode(tab.id)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  mode === tab.id
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
                aria-pressed={mode === tab.id}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full bg-background-200/70 p-1">
            {RANGE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setRange(tab.id)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  range === tab.id
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
                aria-pressed={range === tab.id}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* equity curve */}
      <div className="mt-5">
        <EquityCurve data={equity} pointNoun={pointNoun} totalCount={fullEquity.length} />
      </div>

      {/* drawdown / underwater curve */}
      <div className="mt-6 border-t border-background-200 pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <i className="ri-arrow-down-double-line text-secondary-500" aria-hidden="true"></i>
            <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950">
              Drawdown from peak
            </h4>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900 whitespace-nowrap">
            Deepest in view {maxDrawdown.toFixed(1)}%
          </span>
        </div>
        <p className="mt-1 text-xs md:text-sm text-foreground-500">
          How far the closed-trade balance falls below its previous high (underwater curve).
          Recomputed for the selected view, so it can differ from the headline Max Drawdown,
          which also counts floating equity.
        </p>
        <div className="mt-5">
          <DrawdownCurve data={drawdown} pointNoun={pointNoun} totalCount={fullEquity.length} />
        </div>
      </div>
    </div>
  );
}